import { useLazyQuery } from '@apollo/client';
import { IonRouterLink, useIonPopover } from '@ionic/react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import DateStamp from '@components/dates/DateStamp';
import Checkbox from '@components/fields/Checkbox';
import DateInput from '@components/fields/DateInput';
import EmployerSelect, { SpecialOptions } from '@components/fields/EmployerSelect';
import Select from '@components/fields/Select';
import SearchInput from '@components/fieldsNative/SearchInput';
import FormCondition from '@components/forms/FormCondition';
import Modal from '@components/Modal';
import Nbsp from '@components/Nbsp';
import Icon from '@components/text/Icon';
import BodyCopy from '@components/typography/BodyCopy';
import styles from '@components/UniversalSearch.module.css';
import useGlobalOpener from '@hooks/useGlobalOpener';
import useMyExpert from '@hooks/useMyExpert';
import {
  ExpertGlobalRoleEnum,
  GetJourneysSearchDocument,
  GetJourneysSearchQuery,
  GetJourneysSearchQueryVariables,
  JourneySearchResult,
  JourneySearchResultHitFieldEnum,
  JourneysSearchDataTypeEnum,
  JourneysSearchDateRangeTypeEnum,
  JourneysSearchSortByEnum,
  SortDirectionEnum
} from '@typing/Generated';
import { OpenerType } from '@typing/State';
import { IconName } from '@utils/iconUtils';
import { optionsForJourneySearchDateRangeType, optionsForJourneySearchSortBy } from '@utils/optionUtils';

export enum UniversalSearchSortOptionEnum {
  SCORE = 'SCORE',
  CREATED_AT_ASC = 'CREATED_AT_ASC',
  CREATED_AT_DESC = 'CREATED_AT_DESC',
  LAST_ACTIVITY_AT_ASC = 'LAST_ACTIVITY_AT_ASC',
  LAST_ACTIVITY_AT_DESC = 'LAST_ACTIVITY_AT_DESC'
}

type FormData = {
  dataType: JourneysSearchDataTypeEnum;
  dateRangeEnd: string | null;
  dateRangeStart: string | null;
  dateRangeType: JourneysSearchDateRangeTypeEnum;
  employerId: string | null;
  onlyMyJourneys: boolean;
  search: string;
  sortWithDirection: UniversalSearchSortOptionEnum;
};

type Props = {};

type SearchHitProps = {
  dateRangeEnd: string | null;
  dateRangeStart: string | null;
  dateRangeType: JourneysSearchDateRangeTypeEnum;
  hit: JourneySearchResult;
  sortWithDirection: UniversalSearchSortOptionEnum;
};

type DatePickerProps = {
  form: UseFormReturn<FormData, any, undefined>;
};

const SearchHit = ({ dateRangeEnd, dateRangeStart, dateRangeType, hit, sortWithDirection }: SearchHitProps) => {
  const intl = useIntl();
  const universalSearchOpener = useGlobalOpener(OpenerType.UNIVERSAL_SEARCH);

  const hitType = intl.formatMessage(
    { id: `forms.universalSearch.hitType.${hit.hitField}` },
    { journeyName: hit.careProfileName }
  );

  type RenderDataType = {
    ariaLabelKey: string;
    className: string;
    icon: IconName;
    routerLink: string;
  };

  const renderData: RenderDataType | undefined = useMemo(() => {
    let result: RenderDataType | undefined;

    if ([JourneySearchResultHitFieldEnum.TAG_LIST].includes(hit.hitField)) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.user',
        className: styles.iconPrimary700,
        icon: 'user' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}`
      };
    } else if (
      [
        JourneySearchResultHitFieldEnum.CARE_PROFILE_EMOTIONAL_CONCERNS,
        JourneySearchResultHitFieldEnum.CARE_PROFILE_LOCATION,
        JourneySearchResultHitFieldEnum.CARE_PROFILE_NAME,
        JourneySearchResultHitFieldEnum.CARE_PROFILE_PROVIDER_FINANCIAL_CONCERNS,
        JourneySearchResultHitFieldEnum.CLIENT_LOCATION,
        JourneySearchResultHitFieldEnum.CLIENT_NAME,
        JourneySearchResultHitFieldEnum.MEDICATION_NAME,
        JourneySearchResultHitFieldEnum.PROVIDER_NAME
      ].includes(hit.hitField)
    ) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.penWriting',
        className: styles.iconTeal,
        icon: 'penField' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/care-profile`
      };
    } else if ([JourneySearchResultHitFieldEnum.CARE_PLAN_ACTION_ITEM_TITLE].includes(hit.hitField)) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.checkbox',
        className: styles.iconGreen,
        icon: 'squareCheck' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/care-plan/goals/${hit.goalId}`
      };
    } else if ([JourneySearchResultHitFieldEnum.GOAL_TASK_TITLE].includes(hit.hitField)) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.grayceLogo',
        className: styles.iconPrimary500Background,
        icon: 'logoIcon' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/client-visible-tasks/${hit.hitObjectId}`
      };
    } else if ([JourneySearchResultHitFieldEnum.FILE_RESOURCE_TITLE].includes(hit.hitField)) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.file',
        className: styles.iconPrimary500,
        icon: 'file' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/drive`
      };
    } else if ([JourneySearchResultHitFieldEnum.CHAT_MESSAGE_BODY].includes(hit.hitField)) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.messages',
        className: styles.iconPrimary300,
        icon: 'messages' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/messages?chat=${hit.chatId}`
      };
    } else if (
      [
        JourneySearchResultHitFieldEnum.GOAL_TASK_RESOURCE_DESCRIPTION,
        JourneySearchResultHitFieldEnum.GOAL_TASK_RESOURCE_TITLE
      ].includes(hit.hitField)
    ) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.handHeart',
        className: styles.iconPrimary700Background,
        icon: 'handHoldingHeart' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/client-visible-tasks/${hit.taskId}`
      };
    } else if ([JourneySearchResultHitFieldEnum.GOAL_TITLE].includes(hit.hitField)) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.goal',
        className: styles.iconPrimary300,
        icon: 'thumbtack' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/care-plan/goals/${hit.hitObjectId}`
      };
    } else if (
      [
        JourneySearchResultHitFieldEnum.INTERNAL_NOTE_BODY,
        JourneySearchResultHitFieldEnum.INTERNAL_NOTE_TITLE
      ].includes(hit.hitField)
    ) {
      result = {
        ariaLabelKey: 'accessibility.ariaLabels.stickyNote',
        className: styles.iconPrimary300,
        icon: 'stickyNote' as IconName,
        routerLink: `/expert/journeys/${hit.journeyId}/care-plan`
      };
    }

    return result;
  }, [hit]);

  if (!renderData) {
    return null;
  }

  return (
    <div className={styles.hitRow}>
      <div className={`${styles.hitTypeIcon} ${renderData.className}`}>
        <Icon ariaLabelKey={renderData.ariaLabelKey} icon={renderData.icon} />
      </div>
      <div className={styles.hitAndDateRow}>
        <div className={styles.hitColumn}>
          <div className={styles.hitDescription}>
            <div className={styles.hitOwnerName}>
              <BodyCopy color="neutral500">{hit.ownerName}</BodyCopy>
            </div>
            <div>
              <BodyCopy color="neutral500">
                <FormattedMessage id="dictionary.punctuation.mdash" />
              </BodyCopy>
            </div>
            <div className={styles.hitType}>
              <IonRouterLink onClick={universalSearchOpener.close} routerLink={renderData.routerLink}>
                <BodyCopy color="neutral500">{hitType}</BodyCopy>
              </IonRouterLink>
            </div>
          </div>
          <div className={styles.highlights}>
            {hit.highlights.map(highlight => {
              const purifiedHighlight =
                // eslint-disable-next-line @typescript-eslint/naming-convention
                parse(DOMPurify.sanitize(highlight, { ALLOWED_TAGS: ['em'] }));
              return (
                <div className={styles.highlight} key={highlight}>
                  <BodyCopy color="neutral500">{purifiedHighlight}</BodyCopy>
                </div>
              );
            })}
          </div>
        </div>
        {dateRangeType && (dateRangeStart || dateRangeEnd) ? (
          <>
            {dateRangeType === JourneysSearchDateRangeTypeEnum.CREATED_AT ? (
              <div className={styles.hitDate}>
                <DateStamp absoluteDate showWeekday={false} value={hit.createdAt} />
              </div>
            ) : (
              <div className={styles.hitDate}>
                <DateStamp absoluteDate showWeekday={false} value={hit.lastActivityAt} />
              </div>
            )}
          </>
        ) : (
          <>
            {sortWithDirection && sortWithDirection !== UniversalSearchSortOptionEnum.SCORE && (
              <>
                {[UniversalSearchSortOptionEnum.CREATED_AT_ASC, UniversalSearchSortOptionEnum.CREATED_AT_DESC].includes(
                  sortWithDirection
                ) ? (
                  <div className={styles.hitDate}>
                    <DateStamp absoluteDate showWeekday={false} value={hit.createdAt} />
                  </div>
                ) : (
                  <div className={styles.hitDate}>
                    <DateStamp absoluteDate showWeekday={false} value={hit.lastActivityAt} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const DatePickerPopover = ({ form }: DatePickerProps) => {
  const intl = useIntl();
  const dateRangeTypeOptions = optionsForJourneySearchDateRangeType(intl);

  return (
    <div className={styles.datePickerPopoverWrapper}>
      <Select<FormData>
        control={form.control}
        error={form.formState.errors.dateRangeType}
        name="dateRangeType"
        options={dateRangeTypeOptions}
      />
      <div className={styles.datePickerPopoverDivider}></div>
      <DateInput<FormData>
        control={form.control}
        error={form.formState.errors.dateRangeStart}
        label={intl.formatMessage({ id: 'models.journeysSearch.startDate' })}
        name="dateRangeStart"
      />
      <DateInput<FormData>
        control={form.control}
        error={form.formState.errors.dateRangeEnd}
        label={intl.formatMessage({ id: 'models.journeysSearch.endDate' })}
        name="dateRangeEnd"
      />
    </div>
  );
};

const DatePicker = ({ form }: DatePickerProps) => {
  const [presentPopover] = useIonPopover(<DatePickerPopover form={form} />);

  const dateRangeType = form.watch('dateRangeType');
  const startDate = form.watch('dateRangeStart');
  const endDate = form.watch('dateRangeEnd');

  const selectionCopy = useMemo(() => {
    if (startDate || endDate) {
      if (startDate && endDate) {
        return (
          <div className={styles.datePickerDateRange}>
            <FormattedMessage id={`models.journeysSearch.dateRangeTypes.${dateRangeType}`} />
            <span>
              <DateStamp absoluteDate mmddyyyy showWeekday={false} value={startDate} />
            </span>
            <BodyCopy color="primary700">
              <FormattedMessage id="dictionary.punctuation.mdash" />
            </BodyCopy>
            <span>
              <DateStamp absoluteDate mmddyyyy showWeekday={false} value={endDate} />
            </span>
          </div>
        );
      } else if (startDate) {
        return (
          <>
            <FormattedMessage id={`models.journeysSearch.dateRangeTypes.${dateRangeType}`} />
            <Nbsp />
            <BodyCopy color="primary700">
              <FormattedMessage id="models.journeysSearch.after" />
            </BodyCopy>
            <Nbsp />
            <DateStamp absoluteDate mmddyyyy showWeekday={false} value={startDate} />
          </>
        );
      } else if (endDate) {
        return (
          <>
            <FormattedMessage id={`models.journeysSearch.dateRangeTypes.${dateRangeType}`} />
            <Nbsp />
            <BodyCopy color="primary700">
              <FormattedMessage id="models.journeysSearch.before" />
            </BodyCopy>
            <Nbsp />
            <DateStamp absoluteDate mmddyyyy showWeekday={false} value={endDate} />
          </>
        );
      }
    } else {
      return <FormattedMessage id="dictionary.date" />;
    }
  }, [dateRangeType, endDate, startDate]);

  return (
    <div
      className={styles.datePickerButton}
      onClick={e =>
        presentPopover({ cssClass: styles.datePickerPopover, event: e.nativeEvent, showBackdrop: false, size: 'auto' })
      }
    >
      <Icon ariaLabelKey={'accessibility.ariaLabels.calendar'} className={styles.datePickerIcon} icon="calendarDays" />
      {selectionCopy}
    </div>
  );
};

const UniversalSearch = ({}: Props) => {
  const universalSearchOpener = useGlobalOpener(OpenerType.UNIVERSAL_SEARCH);
  const intl = useIntl();
  const myExpert = useMyExpert();

  const initialValues: FormData = {
    dataType: JourneysSearchDataTypeEnum.JOURNEYS,
    dateRangeEnd: null,
    dateRangeStart: null,
    dateRangeType: JourneysSearchDateRangeTypeEnum.CREATED_AT,
    employerId: null,
    onlyMyJourneys: false,
    search: '',
    sortWithDirection: UniversalSearchSortOptionEnum.SCORE
  };

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange'
  });

  const [getJourneysSearch, getJourneysSearchQuery] = useLazyQuery<
    GetJourneysSearchQuery,
    GetJourneysSearchQueryVariables
  >(GetJourneysSearchDocument, {
    fetchPolicy: 'no-cache'
  });

  const dataType = form.watch('dataType');
  const dateRangeEnd = form.watch('dateRangeEnd');
  const dateRangeStart = form.watch('dateRangeStart');
  const dateRangeType = form.watch('dateRangeType');
  const employerId = form.watch('employerId');
  const onlyMyJourneys = form.watch('onlyMyJourneys');
  const search = form.watch('search');
  const sortWithDirection = form.watch('sortWithDirection');

  useEffect(() => {
    if (!search) {
      return;
    }

    let sortBy: JourneysSearchSortByEnum | undefined;
    let sortDirection: SortDirectionEnum | undefined;

    if (sortWithDirection === UniversalSearchSortOptionEnum.CREATED_AT_ASC) {
      sortBy = JourneysSearchSortByEnum.CREATED_AT;
      sortDirection = SortDirectionEnum.ascending;
    } else if (sortWithDirection === UniversalSearchSortOptionEnum.CREATED_AT_DESC) {
      sortBy = JourneysSearchSortByEnum.CREATED_AT;
      sortDirection = SortDirectionEnum.descending;
    } else if (sortWithDirection === UniversalSearchSortOptionEnum.LAST_ACTIVITY_AT_ASC) {
      sortBy = JourneysSearchSortByEnum.LAST_ACTIVITY_AT;
      sortDirection = SortDirectionEnum.ascending;
    } else if (sortWithDirection === UniversalSearchSortOptionEnum.LAST_ACTIVITY_AT_DESC) {
      sortBy = JourneysSearchSortByEnum.LAST_ACTIVITY_AT;
      sortDirection = SortDirectionEnum.descending;
    } else if (sortWithDirection === UniversalSearchSortOptionEnum.SCORE) {
      sortBy = JourneysSearchSortByEnum.SCORE;
      sortDirection = SortDirectionEnum.descending;
    }

    let selfPaid = false;
    let useEmployerId = employerId;
    if (useEmployerId === SpecialOptions.SELF_PAID) {
      selfPaid = true;
      useEmployerId = null;
    }

    getJourneysSearch({
      variables: {
        count: 25,
        dataType,
        dateRangeEnd: dateRangeEnd || null,
        dateRangeStart: dateRangeStart || null,
        dateRangeType,
        employerId,
        onlyMyJourneys,
        search,
        selfPaid,
        sortBy,
        sortDirection
      }
    });
  }, [
    dataType,
    dateRangeEnd,
    dateRangeStart,
    dateRangeType,
    employerId,
    getJourneysSearch,
    onlyMyJourneys,
    search,
    sortWithDirection
  ]);

  const dataTypeOptions = useMemo(() => {
    const options = [
      { iconClass: styles.iconPrimary700, iconName: 'user', option: JourneysSearchDataTypeEnum.JOURNEYS },
      { iconClass: styles.iconTeal, iconName: 'penField', option: JourneysSearchDataTypeEnum.PROFILE_DATA },
      { iconClass: styles.iconPrimary300, iconName: 'messages', option: JourneysSearchDataTypeEnum.MESSAGES },
      {
        iconClass: styles.iconGreen,
        iconName: 'squareCheck',
        option: JourneysSearchDataTypeEnum.CARE_PLAN_ACTION_ITEMS
      },
      { iconClass: styles.iconPrimary300, iconName: 'thumbtack', option: JourneysSearchDataTypeEnum.GOALS },
      {
        iconClass: styles.iconPrimary700Background,
        iconName: 'handHoldingHeart',
        option: JourneysSearchDataTypeEnum.RESOURCES
      },
      { iconClass: styles.iconPrimary500Background, iconName: 'logoIcon', option: JourneysSearchDataTypeEnum.TASKS },
      { iconClass: styles.iconPrimary500, iconName: 'file', option: JourneysSearchDataTypeEnum.FILE_RESOURCES },
      { iconClass: styles.iconPrimary300, iconName: 'stickyNote', option: JourneysSearchDataTypeEnum.INTERNAL_NOTES }
    ];
    return options.map(kindOption => ({
      iconClassName: kindOption.iconClass,
      iconName: kindOption.iconName as IconName,
      label: intl.formatMessage({ id: `models.journeysSearch.dataType.${kindOption.option}` }),
      value: kindOption.option
    }));
  }, [intl]);

  const sortByOptions = optionsForJourneySearchSortBy(intl);

  return (
    <Modal cssClass={styles.modal} hideTitleBorder opener={universalSearchOpener}>
      {({}) => (
        <>
          <div className={styles.searchInputWrapper}>
            <SearchInput
              error={form.formState.errors.search?.message}
              initialValue={search}
              name="search"
              setValue={v => form.setValue('search', v)}
            />
          </div>
          <div className={styles.optionsRow}>
            <div className={styles.option}>
              <Select<FormData>
                control={form.control}
                error={form.formState.errors.sortWithDirection}
                name="sortWithDirection"
                options={sortByOptions}
              />
            </div>
            <div className={styles.option}>
              <Select<FormData>
                control={form.control}
                error={form.formState.errors.dataType}
                name="dataType"
                options={dataTypeOptions}
              />
            </div>
            <DatePicker form={form} />
          </div>

          <div className={styles.optionsRow}>
            <div className={styles.option}>
              <EmployerSelect<FormData>
                allowAny
                allowSelfPaid
                anyOptionLabelKey="forms.journeysSearch.anyEmployer"
                control={form.control}
                error={form.formState.errors.employerId}
                initialOption={{
                  label: undefined,
                  value: null
                }}
                name="employerId"
              />
            </div>
            <FormCondition
              condition={myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager}
              fieldName="onlyMyJourneys"
              form={form}
            >
              <Checkbox<FormData>
                control={form.control}
                error={form.formState.errors.onlyMyJourneys}
                name="onlyMyJourneys"
              >
                <>
                  <FormattedMessage id="forms.journeysSearch.onlyMyJourneys" />
                </>
              </Checkbox>
            </FormCondition>
          </div>

          {getJourneysSearchQuery.data?.journeysSearch.length === 0 ? (
            <div className={styles.noResults}>
              <FormattedMessage id="models.journeysSearch.noResults" />
            </div>
          ) : (
            <>
              {search &&
                getJourneysSearchQuery.data?.journeysSearch.map(hit => (
                  <SearchHit
                    dateRangeEnd={dateRangeEnd}
                    dateRangeStart={dateRangeStart}
                    dateRangeType={dateRangeType}
                    hit={hit}
                    key={hit.id}
                    sortWithDirection={sortWithDirection}
                  />
                ))}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default UniversalSearch;
